import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate, formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash, MagnifyingGlass, Buildings, ClipboardText, CheckCircle, Clock, ArrowsClockwise, Printer } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

const STATUS_MAP = {
  u_toku: { label: 'U toku', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  odobren: { label: 'Odobreno', color: 'bg-green-100 text-green-700 border-green-300' },
  zavrsen: { label: 'Završeno', color: 'bg-slate-100 text-slate-600 border-slate-300' },
};

const DEFAULT_PREDMETI = [
  'Brisanje iz registra',
  'Brisanje iz registra PDV obveznika',
  'Evidencija ovlašćenih lica',
  'Izdavanje izvod iz CRPS',
  'Izdavanje uvjerenja iz poreskih evidencija',
  'Izmjena podataka',
  'Odjava osiguranika',
  'Potvrda podataka',
  'Prijava osiguranika',
  'Registracija DOO',
  'Registracija preduzetnika',
  'Registracija stvarnih vlasnika',
  'Rezervacija/prenos naziva',
  'Upis u registar PDV obveznika',
];

export default function IRMSZahtjeviPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPredmet, setFilterPredmet] = useState('all');
  const [PREDMETI, setPREDMETI] = useState(DEFAULT_PREDMETI);

  useEffect(() => {
    api.get('/settings/config-lists').then(r => {
      const list = r.data?.irms_predmeti;
      if (Array.isArray(list) && list.length > 0) setPREDMETI(list);
    }).catch(() => {});
  }, []);

  const emptyForm = {
    client_id: '', naziv_firme: '', pib: '', naziv_predmeta: '',
    broj_predmeta: '', datum_pocetka: new Date().toISOString().split('T')[0],
    datum_zavrsetka: '',
    status: 'u_toku', napomena: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [z, c] = await Promise.all([api.get('/irms-zahtjevi'), api.get('/clients')]);
      setEntries(z.data);
      setClients(c.data.sort((a, b) => (a.skraceni_naziv || '').localeCompare(b.skraceni_naziv || '')));
    } catch { toast.error('Greška pri učitavanju'); }
    finally { setLoading(false); }
  };

  const handleClientSelect = (cid) => {
    if (cid === 'none') return;
    const c = clients.find(cl => cl.id === cid);
    if (c) setForm(prev => ({
      ...prev, client_id: cid,
      naziv_firme: c.skraceni_naziv || c.naziv_firme || '',
      pib: c.pib || '',
    }));
  };

  const handleSubmit = async () => {
    if (!form.naziv_firme || !form.naziv_predmeta) {
      toast.error('Unesite naziv firme i naziv predmeta');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/irms-zahtjevi/${editingId}`, form);
        toast.success('Zahtjev ažuriran');
      } else {
        await api.post('/irms-zahtjevi', form);
        toast.success('Zahtjev kreiran');
      }
      setIsFormOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (z) => {
    setEditingId(z.id);
    setForm({
      client_id: z.client_id || '', naziv_firme: z.naziv_firme || '',
      pib: z.pib || '', naziv_predmeta: z.naziv_predmeta || '',
      broj_predmeta: z.broj_predmeta || '',
      datum_pocetka: z.datum_pocetka || '',
      datum_zavrsetka: z.datum_zavrsetka || '',
      status: z.status || 'u_toku', napomena: z.napomena || '',
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/irms-zahtjevi/${id}`); toast.success('Obrisano'); fetchData(); }
    catch { toast.error('Greška'); }
  };

  const filtered = useMemo(() => {
    let list = entries;
    if (filterStatus !== 'all') list = list.filter(e => e.status === filterStatus);
    if (filterPredmet !== 'all') list = list.filter(e => e.naziv_predmeta === filterPredmet);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(e =>
        (e.naziv_firme || '').toLowerCase().includes(s) ||
        (e.pib || '').includes(s) ||
        (e.broj_predmeta || '').toLowerCase().includes(s) ||
        (e.naziv_predmeta || '').toLowerCase().includes(s)
      );
    }
    return [...list].sort((a, b) => (b.datum_pocetka || '').localeCompare(a.datum_pocetka || ''));
  }, [entries, filterStatus, filterPredmet, search]);

  // Stats
  const stats = useMemo(() => ({
    total: entries.length,
    u_toku: entries.filter(e => e.status === 'u_toku').length,
    odobren: entries.filter(e => e.status === 'odobren').length,
    zavrsen: entries.filter(e => e.status === 'zavrsen').length,
  }), [entries]);

  const handlePrint = () => {
    const statusLabel = { u_toku: 'U toku', odobren: 'Odobreno', zavrsen: 'Završeno' };
    const fD = (d) => formatDate(d);
    const rows = filtered.map((z, i) => `
      <tr>
        <td style="border:1px solid #999;padding:4px 6px;text-align:center;">${i + 1}</td>
        <td style="border:1px solid #999;padding:4px 6px;">${z.naziv_firme || '-'}</td>
        <td style="border:1px solid #999;padding:4px 6px;">${z.pib || '-'}</td>
        <td style="border:1px solid #999;padding:4px 6px;">${z.naziv_predmeta || '-'}</td>
        <td style="border:1px solid #999;padding:4px 6px;font-size:8pt;">${z.broj_predmeta || '-'}</td>
        <td style="border:1px solid #999;padding:4px 6px;text-align:center;">${fD(z.datum_pocetka)}</td>
        <td style="border:1px solid #999;padding:4px 6px;text-align:center;">${fD(z.datum_zavrsetka)}</td>
        <td style="border:1px solid #999;padding:4px 6px;">${z.created_by_name || '-'}</td>
        <td style="border:1px solid #999;padding:4px 6px;text-align:center;">${statusLabel[z.status] || z.status}</td>
      </tr>`).join('');

    const printDiv = document.createElement('div');
    printDiv.id = 'irms-print-area';
    printDiv.innerHTML = `
      <style>
        @media print {
          body > *:not(#irms-print-area) { display: none !important; }
          #irms-print-area { display: block !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
        #irms-print-area { display: none; font-family: Arial, sans-serif; font-size: 9pt; }
        #irms-print-area h2 { text-align: center; margin-bottom: 3px; font-size: 13pt; }
        #irms-print-area .sub { text-align: center; font-size: 9pt; color: #666; margin-bottom: 10px; }
        #irms-print-area table { width: 100%; border-collapse: collapse; }
        #irms-print-area th { border: 1px solid #999; padding: 5px 6px; background: #f0f0f0; font-size: 8pt; text-align: center; }
      </style>
      <h2>IRMS Zahtjevi - FINANCE GROUP DOO</h2>
      <p class="sub">Zahtjevi prema Poreskoj upravi | Stampano: ${formatDate(new Date().toISOString())}</p>
      <table>
        <thead><tr><th>#</th><th>Naziv firme</th><th>PIB</th><th>Naziv predmeta</th><th>Broj predmeta</th><th>Datum</th><th>Zavrseno</th><th>Podneo</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    document.body.appendChild(printDiv);
    window.print();
    setTimeout(() => printDiv.remove(), 500);
  };

  return (
    <div className="space-y-6" data-testid="irms-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">IRMS Zahtjevi</h3>
          <p className="text-sm text-slate-600 mt-1">Zahtjevi prema Poreskoj upravi</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handlePrint} variant="outline" className="text-green-600 border-green-300 hover:bg-green-50" data-testid="irms-print-btn">
            <Printer size={18} className="mr-2" /> Štampaj
          </Button>
          <Button onClick={() => { setForm(emptyForm); setEditingId(null); setIsFormOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="irms-add-btn">
            <Plus size={18} className="mr-2" /> Novi zahtjev
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-3 text-center"><p className="text-2xl font-bold text-slate-800">{stats.total}</p><p className="text-xs text-slate-500">Ukupno</p></Card>
        <Card className="p-3 text-center bg-blue-50 border-blue-200"><p className="text-2xl font-bold text-blue-700">{stats.u_toku}</p><p className="text-xs text-blue-600">U toku</p></Card>
        <Card className="p-3 text-center bg-green-50 border-green-200"><p className="text-2xl font-bold text-green-700">{stats.odobren}</p><p className="text-xs text-green-600">Odobreno</p></Card>
        <Card className="p-3 text-center bg-slate-50 border-slate-200"><p className="text-2xl font-bold text-slate-600">{stats.zavrsen}</p><p className="text-xs text-slate-500">Završeno</p></Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..."
              className="pl-9 bg-white border-slate-300 text-sm" data-testid="irms-search" />
          </div>
          <Select value={filterPredmet} onValueChange={setFilterPredmet}>
            <SelectTrigger className="bg-white border-slate-300 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi predmeti</SelectItem>
              {PREDMETI.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="bg-white border-slate-300 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi statusi</SelectItem>
              <SelectItem value="u_toku">U toku</SelectItem>
              <SelectItem value="odobren">Odobreno</SelectItem>
              <SelectItem value="zavrsen">Završeno</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500 self-center">{filtered.length} od {entries.length}</p>
        </div>
      </Card>

      {/* Table */}
      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <Buildings size={48} weight="duotone" className="text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{entries.length === 0 ? 'Nema zahtjeva' : 'Nema rezultata za filter'}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="irms-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Naziv firme</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">PIB</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Naziv predmeta</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Broj predmeta</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Završeno</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Podneo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(z => {
                  const st = STATUS_MAP[z.status] || STATUS_MAP.u_toku;
                  return (
                    <tr key={z.id} className="border-b hover:bg-slate-50" data-testid={`irms-row-${z.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{z.naziv_firme || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{z.pib || '-'}</td>
                      <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate" title={z.naziv_predmeta}>{z.naziv_predmeta || '-'}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{z.broj_predmeta || '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDatePrint(z.datum_pocetka) || '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDatePrint(z.datum_zavrsetka) || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{z.created_by_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${st.color}`}>
                          {z.status === 'u_toku' && <ArrowsClockwise size={12} />}
                          {z.status === 'odobren' && <CheckCircle size={12} />}
                          {z.status === 'zavrsen' && <ClipboardText size={12} />}
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => handleEdit(z)} className="p-2 hover:bg-blue-100 rounded" data-testid={`irms-edit-${z.id}`}><Pencil size={16} className="text-blue-600" /></button>
                          <button onClick={() => handleDelete(z.id)} className="p-2 hover:bg-red-100 rounded" data-testid={`irms-delete-${z.id}`}><Trash size={16} className="text-red-600" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg" data-testid="irms-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi zahtjev' : 'Novi IRMS zahtjev'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Izaberi klijenta (auto-popunjava)</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (handleClientSelect)(id || 'none')} testid="irms-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Naziv firme *</Label>
                <Input value={form.naziv_firme} onChange={e => setForm({...form, naziv_firme: e.target.value})} className="bg-white border-slate-300 text-sm" data-testid="irms-naziv-firme" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">PIB</Label>
                <Input value={form.pib} onChange={e => setForm({...form, pib: e.target.value})} className="bg-white border-slate-300 text-sm" data-testid="irms-pib" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Naziv predmeta *</Label>
              <Select value={form.naziv_predmeta || 'none'} onValueChange={v => setForm({...form, naziv_predmeta: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="irms-predmet-select"><SelectValue placeholder="Izaberite predmet" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Izaberite --</SelectItem>
                  {PREDMETI.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Broj predmeta</Label>
                <Input value={form.broj_predmeta} onChange={e => setForm({...form, broj_predmeta: e.target.value})} placeholder="npr: 02/03-214/2026-51455" className="bg-white border-slate-300 text-sm" data-testid="irms-broj" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Datum početka</Label>
                <DateInput value={form.datum_pocetka} onChange={e => setForm({...form, datum_pocetka: e.target.value})} className="border-slate-300 text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Datum završetka</Label>
              <DateInput value={form.datum_zavrsetka} onChange={e => setForm({...form, datum_zavrsetka: e.target.value})} className="border-slate-300 text-sm" data-testid="irms-datum-zavrsetka" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Status</Label>
              <div className="flex gap-2">
                {[
                  { val: 'u_toku', label: 'U toku', cls: 'bg-blue-600 text-white border-blue-600', off: 'bg-white text-blue-600 border-blue-300' },
                  { val: 'odobren', label: 'Odobreno', cls: 'bg-green-600 text-white border-green-600', off: 'bg-white text-green-600 border-green-300' },
                  { val: 'zavrsen', label: 'Završeno', cls: 'bg-slate-600 text-white border-slate-600', off: 'bg-white text-slate-600 border-slate-300' },
                ].map(s => (
                  <button key={s.val} type="button" onClick={() => setForm({...form, status: s.val})}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border-2 transition-colors ${form.status === s.val ? s.cls : s.off}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Napomena</Label>
              <textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})}
                placeholder="Opciona napomena..."
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setIsFormOpen(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="irms-submit-btn">
                {editingId ? 'Ažuriraj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
